import { useContext, useRef, useState, useEffect, useCallback } from "react";
import { normalizeUsagePlanFormData } from "../../utils/usagePlanNormalize";
import ExcelPresenter from "./ExcelPresenter";
import { api } from "../../config/api";
import { logEvent } from "../../services/eventLog";
import AlertContext from "../../contexts/AlertContext";
import { util } from "../../config/util";
import { LoadingContext } from "../../contexts/LoadingContext";
import { ExcelProvider, useExcelContext } from "../../contexts/ExcelContext";
import { ExcelX2File, ExcelX2FileSheet } from "../../models/excelModel/excelX2";
import { NON_STATEMENT_SHEET, keepSheetsWithinLimit } from "../../utils/x2Eta";
import { useHistory, useLocation } from "react-router-dom";
import { pages } from "../../constants/PagePaths";
import { CategoryItem } from "../../models/CategoryItem";
import { ExcelItem } from "../../models/excelModel/excelItem";
import { PresetItem } from "../../models/PresetItem";
import { SafetyManagementItemData } from "../../constants/SafetyManagementItemData";
import { EnvironmentalItemData } from "../../constants/EnvironmentalItemData";
// 자동 시트 선택 기능 import
import { ServiceType, isFileRequiredService } from "../../constants/ServiceSheetMapping";
import {
  autoSelectSheets,
  generateNotificationMessage,
  clearAllSheetSelections,
  calculateSelectedSheetCells,
  logAutoSelectionProcess
} from "../../utils/sheetAutoSelection";
import { useExcelTaskPolling } from "../../hooks/useExcelTaskPolling";
import ExcelTaskPanel from "../../components/ExcelTaskPanel";
import {
  getExcelErrorMessage,
  isExcelOverloadError,
} from "../../utils/excelErrorMessage";
import { computeFileSha256 } from "../../utils/fileHash";

// 엑셀 자동화 화면의 선택 작업을 주소에 담는 키 (예: /excel?item=change-3)
export const EXCEL_ITEM_QUERY_KEY = "item";
const DEFAULT_EXCEL_ITEM_ID = "start-1";
const initialExcelItemIdFromUrl = (): string => {
  try {
    return new URLSearchParams(window.location.search).get(EXCEL_ITEM_QUERY_KEY) || "";
  } catch (error) {
    return "";
  }
};

const ExcelContainerInner = () => {
  const [originOption, setOriginOption] = useState("black");
  const [divideOption, setDivideOption] = useState("fixed");
  const [isOriginUpper, setIsOriginUpper] = useState(true);
  // 두줄 나누기: 당초 줄(열)에도 수식을 남길지. 기본 false = 값(당초 고정). 체크한 경우만 서버가 수식으로 복원 (10-15)
  const [keepOriginFormulas, setKeepOriginFormulas] = useState(false);
  // 증감내역서: 증(+)/감(-) 분리 컬럼 양식(공사비물량대비표/공사비증감대비표) 사용 여부
  const [useSeparatedColumns, setUseSeparatedColumns] = useState(false);
  const [isHiddenSheetShow, setIsHiddenSheetShow] = useState(false);
  const [gapjiOption, setGapjiOption] = useState("없음");
  const [divideDirectionOption, setDivideDirectionOption] =
    useState("vertical");
  const [includeReestimationSheet, setIncludeReestimationSheet] =
    useState(true);
  const [selectedCategoryIndex, setSelectedCategoryIndex] = useState(0);
  const [selectedItemId, setSelectedItemId] = useState(
    initialExcelItemIdFromUrl() || DEFAULT_EXCEL_ITEM_ID
  ); // 기본값: 계약/착공내역서. 주소에 ?item= 이 있으면 그 작업을 복원
  const [fileInfos, setFileInfos] = useState<ExcelX2File[]>([]);
  const [expandedFiles, setExpandedFiles] = useState<Set<number>>(new Set());
  const [advancePayment, setAdvancePayment] = useState<string>(""); // 선급금 상태
  const [completionSettlementAmount, setCompletionSettlementAmount] =
    useState<string>(""); // 준공정산금액 상태
  const [claimDate, setClaimDate] = useState<string>(""); // 청구 일자 상태
  const [claimAmount, setClaimAmount] = useState<string>(""); // 청구 금액 상태

  // 선급금 신청서 관련 상태

  const [contractMaterialCost, setContractMaterialCost] = useState<string>(""); // 계약 재료비
  const [contractIndirectLaborCost, setContractIndirectLaborCost] =
    useState<string>(""); // 계약 간접노무비
  const [contractExpenses, setContractExpenses] = useState<string>(""); // 계약경비
  const [contractOther, setContractOther] = useState<string>(""); // 계약 기타
  const [
    prepaymentAdjustmentMaterialCost,
    setPrepaymentAdjustmentMaterialCost,
  ] = useState<string>(""); // 선급금 조정 재료비
  const [
    prepaymentAdjustmentIndirectLaborCost,
    setPrepaymentAdjustmentIndirectLaborCost,
  ] = useState<string>(""); // 선급금 조정 간접노무비
  const [prepaymentExpenses, setPrepaymentExpenses] = useState<string>(""); // 선급금 경비
  const [prepaymentOther, setPrepaymentOther] = useState<string>(""); // 선급금 기타

  // 예정공정표 관련 상태
  const [scheduleData, setScheduleData] = useState<any>({});

  // 차수분 내역서 관련 상태
  const [phaseCount, setPhaseCount] = useState<number>(2);
  const [phaseTexts, setPhaseTexts] = useState<string[]>(["1차", "2차"]);
  const [totalText, setTotalText] = useState<string>("전체");
  const [remainderText, setRemainderText] = useState<string>("잔여");
  const [useExistingFiles, setUseExistingFiles] = useState<boolean>(true);
  const [phaseFiles, setPhaseFiles] = useState<any[]>([]);
  const [isLoadingPhaseSheets, setIsLoadingPhaseSheets] = useState<boolean>(false);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const phaseFileInputRefs = useRef<{ [key: number]: HTMLInputElement | null }>({});

  // 프리셋 관련 상태
  const [presets, setPresets] = useState<PresetItem[]>([]);
  const [isPresetModalOpen, setIsPresetModalOpen] = useState(false);
  const [currentPresetType, setCurrentPresetType] = useState<
    "fieldRepresentative" | "companyInfo" | "projectInfo"
  >("companyInfo");
  const [editingPreset, setEditingPreset] = useState<PresetItem | null>(null);

  // 선택된 프리셋들 관리
  const [selectedPresets, setSelectedPresets] = useState<{
    fieldRepresentative?: PresetItem;
    companyInfo?: PresetItem;
    projectInfo?: PresetItem;
  }>({});

  const alertContext = useContext(AlertContext);
  const loading = useContext(LoadingContext);
  const history = useHistory();
  const routerLocation = useLocation();

  // 비동기 엑셀 작업 폴링
  const { tasks: excelTasks, addTasks: addExcelTasks, loadExistingTasks, clearTasks: clearExcelTasks, cancelTask: cancelExcelTask, cancelAllTasks: cancelAllExcelTasks, deleteTask: deleteExcelTask, retryTask: retryExcelTask, hasActiveTasks } = useExcelTaskPolling();

  // 페이지 로드 시 기존 처리내역 복원
  useEffect(() => {
    if (util.isLogin()) {
      loadExistingTasks();
    }
  }, [loadExistingTasks]);

  // 로컬스토리지에서 기본 프리셋 가져오기
  const getDefaultPresetsFromStorage = () => {
    try {
      const savedDefaultPresets = localStorage.getItem("excel-default-presets");
      if (savedDefaultPresets) {
        return JSON.parse(savedDefaultPresets);
      }
    } catch (error) {
      console.error("기본 프리셋 불러오기 실패:", error);
    }
    return {};
  };

  // 로컬스토리지에 기본 프리셋 저장하기
  const saveDefaultPresetsToStorage = (defaultPresets: any) => {
    try {
      localStorage.setItem(
        "excel-default-presets",
        JSON.stringify(defaultPresets)
      );
    } catch (error) {
      console.error("기본 프리셋 저장 실패:", error);
    }
  };
  const megaByte = 1000 * 1000;
  const fileCountLimit = 3;
  const fileSizeSum = useRef(0);
  const sheetCellSum = useRef(0);

  // 서비스 ID별 필요 이용권 매핑
  const servicePermissionMapper: Record<string, string | string[]> = {
    // EXCEL_CONTRACT 필요
    "start-4": "EXCEL_CONTRACT",
    "start-2-1": "EXCEL_CONTRACT",
    "start-2-2": "EXCEL_CONTRACT",
    "start-2-3": "EXCEL_CONTRACT",
    "start-2-4": "EXCEL_CONTRACT",
    "start-3": "EXCEL_CONTRACT",
    // EXCEL_X2 필요
    "change-4": "EXCEL_X2",
    "change-5": "EXCEL_X2",
    "change-6": "EXCEL_X2",
    "change-8": "EXCEL_X2",
    "completion-2-1": "EXCEL_X2",
    "completion-2-2": "EXCEL_X2",
    "completion-2-3": "EXCEL_X2",
    // 청구서류는 EXCEL_CONTRACT 또는 EXCEL_X2 둘 중 하나만 있으면 사용 가능
    "billing-1": ["EXCEL_CONTRACT", "EXCEL_X2"],
  };

  const categoryPermissionMapper: any = {
    0: "EXCEL_X2",
    1: "EXCEL_FLUCTUATION",
    2: "EXCEL_REPORT",
    3: "EXCEL_CONTRACT",
    4: "EXCEL_READY",
    5: "free",
    6: "free",
    7: "free",
  };
  const macroCategories = [
    {
      id: "contract",
      title: "계약/착공",
      icon: "description",
      items: [
        {
          id: "start-1",
          title: "계약/착공내역서",
          description: "엑셀파일의 계약/착공내역서 작성을 손쉽게 처리하세요.",
          lectureId: "49",
          originalIndex: 3,
          requiresFile: true,
          requiresPreset: false,
        },
        {
          id: "start-4",
          title: "계약서류",
          description: "계약 관련 서류를 생성합니다.",
          lectureId: "",
          originalIndex: null,
          requiresFile: false,
          requiresPreset: true,
        },
        {
          id: "start-2",
          title: "착공서류",
          description: "착공 관련 서류를 생성합니다.",
          lectureId: "",
          originalIndex: null,
          subItems: [
            {
              id: "start-2-1",
              title: "착공계약서류",
              description: "착공계약 관련 서류를 생성합니다.",
              lectureId: "",
              originalIndex: null,
              requiresFile: false,
              requiresPreset: true,
            },
            {
              id: "start-2-2",
              title: "안전관리비 사용 계획서",
              description: "안전관리비 사용계획 내역을 작성합니다.",
              lectureId: "",
              originalIndex: null,
              requiresFile: false,
              requiresPreset: true,
              isFormInput: true,
            },
            {
              id: "start-2-3",
              title: "환경보전비 사용 계획서",
              description: "환경보전비 사용 내역서를 작성합니다.",
              lectureId: "",
              originalIndex: null,
              requiresFile: false,
              requiresPreset: true,
              isFormInput: true,
            },
            {
              id: "start-2-4",
              title: "건설공사의 직접시공계획서",
              description:
                "건설산업안전 시행규칙에 따른 직접시공계획서를 작성합니다.",
              lectureId: "",
              originalIndex: null,
              requiresFile: false,
              requiresPreset: true,
            },
          ],
        },
        {
          id: "start-3",
          title: "선급금 신청서",
          description: "선급금 신청 관련 서류를 생성합니다.",
          lectureId: "",
          originalIndex: null,
          requiresFile: false,
          requiresPreset: true,
        },
      ],
    },
    {
      id: "change",
      title: "변경",
      icon: "edit",
      items: [
        {
          id: "change-3",
          title: "두 줄로 나누기",
          description: "엑셀파일의 당초/변경을 손쉽게 처리하세요.",
          lectureId: "50",
          originalIndex: 0,
          requiresFile: true,
          requiresPreset: false,
        },
        {
          id: "change-7",
          title: "증감 + 실정보고서",
          description: "엑셀파일의 증감 표시와 실정보고서를 손쉽게 처리하세요.",
          lectureId: "52",
          originalIndex: 7,
          requiresFile: true,
          requiresPreset: false,
        },
        {
          id: "change-2",
          title: "증감 내역서",
          description: "엑셀파일의 증감 표시를 손쉽게 처리하세요.",
          lectureId: "52",
          originalIndex: 1,
          requiresFile: true,
          requiresPreset: false,
        },
        {
          id: "change-1",
          title: "변경 실정보고서",
          description: "변경 엑셀파일의 실정보고서 작성을 손쉽게 처리하세요.",
          lectureId: "58",
          originalIndex: 2,
          requiresFile: true,
          requiresPreset: false,
        },

        {
          id: "change-4",
          title: "변경 준공만들기",
          description: "변경 엑셀파일의 두줄을 서로 같게 만들어 드립니다.",
          lectureId: "52",
          originalIndex: 5,
          requiresFile: true,
          requiresPreset: false,
        },
        {
          id: "change-5",
          title: "변경 한줄만들기",
          description: "변경 엑셀파일의 두줄을 한줄로 손쉽게 만드세요.",
          lectureId: "52",
          originalIndex: 6,
          requiresFile: true,
          requiresPreset: false,
        },
        {
          id: "change-6",
          title: "변경 색상교체",
          description:
            "변경 엑셀파일의 당초/변경행 색상교체를 손쉽게 처리하세요.",
          lectureId: "52",
          originalIndex: 7,
          requiresFile: true,
          requiresPreset: false,
        },
        {
          id: "change-8",
          title: "차수분 내역서",
          description:
            "여러 차수의 기성내역서를 하나의 통합 파일로 만들어 드립니다.",
          lectureId: "",
          originalIndex: null,
          requiresFile: true,
          requiresPreset: false,
          isFormInput: false,
        },
      ],
    },
    {
      id: "progress",
      title: "기성",
      icon: "assignment",
      items: [
        {
          id: "progress-1",
          title: "기성 내역서",
          description: "엑셀파일의 기성내역서 작성을 손쉽게 처리하세요.",
          lectureId: "30",
          originalIndex: 4,
          requiresFile: true,
          requiresPreset: false,
        },
      ],
    },
    {
      id: "completion",
      title: "준공",
      icon: "check_circle",
      items: [
        {
          id: "completion-1",
          title: "준공내역서",
          description: "준공 관련 내역서를 작성합니다.",
          lectureId: "",
          originalIndex: 0,
          requiresFile: true,
          requiresPreset: false,
        },
        {
          id: "completion-2",
          title: "준공서류",
          description: "준공 관련 서류를 생성합니다.",
          lectureId: "",
          originalIndex: null,
          subItems: [
            {
              id: "completion-2-1",
              title: "준공계서류",
              description: "준공계 관련 서류를 작성합니다.",
              lectureId: "",
              originalIndex: null,
              requiresFile: false,
              requiresPreset: true,
            },
            {
              id: "completion-2-2",
              title: "안전관리비 사용 내역서",
              description: "안전관리비 사용계획 내역을 작성합니다.",
              lectureId: "",
              originalIndex: null,
              requiresFile: false,
              requiresPreset: true,
              isFormInput: true,
            },
            {
              id: "completion-2-3",
              title: "환경보전비 사용 내역서",
              description: "환경보전비 사용 내역서를 작성합니다.",
              lectureId: "",
              originalIndex: null,
              requiresFile: false,
              requiresPreset: true,
              isFormInput: true,
            },
          ],
        },
      ],
    },
    {
      id: "billing",
      title: "청구",
      icon: "receipt",
      items: [
        {
          id: "billing-1",
          title: "청구서류",
          description: "청구 관련 서류를 생성합니다.",
          lectureId: "",
          originalIndex: null,
          requiresFile: false,
          requiresPreset: true,
        },
      ],
    },
  ];

  // 기존 호환성을 위한 macroType (선택된 항목의 정보를 제공)
  const macroType: ExcelItem[] = [
    {
      id: "0",
      title: "두 줄로 나누기",
      description: "엑셀파일의 당초/변경을 손쉽게 처리하세요.",
      lectureId: "50",
    },
    {
      id: "1",
      title: "증감 내역서",
      description: "엑셀파일의 증감 표시를 손쉽게 처리하세요.",
      lectureId: "52",
    },
    {
      id: "2",
      title: "변경 실정보고서",
      description: "변경 엑셀파일의 실정보고서 작성을 손쉽게 처리하세요.",
      lectureId: "58",
    },
    {
      id: "3",
      title: "계약 내역서",
      description: "엑셀파일의 계약내역서 작성을 손쉽게 처리하세요.",
      lectureId: "49",
    },
    {
      id: "4",
      title: "기성 내역서",
      description: "엑셀파일의 기성내역서 작성을 손쉽게 처리하세요.",
      lectureId: "30",
    },
    {
      id: "5",
      title: "변경 준공만들기",
      description: "변경 엑셀파일의 두줄을 서로 같게 만들어 드립니다.",
      lectureId: "52",
    },
    {
      id: "6",
      title: "변경 한줄만들기",
      description: "변경 엑셀파일의 두줄을 한줄로 손쉽게 만드세요.",
      lectureId: "52",
    },
    {
      id: "7",
      title: "변경 색상교체",
      description: "변경 엑셀파일의 당초/변경행 색상교체를 손쉽게 처리하세요.",
      lectureId: "52",
    },
  ];
  /** 파일 사이즈 총 합 제한
   * 개당 사이즈 곱하기 개수로 제한
   */
  const fileSizeLimit = 30 * fileCountLimit;

  // 선택된 항목의 정보를 가져오는 함수
  const getSelectedItemInfo = () => {
    for (const category of macroCategories) {
      for (const item of category.items) {
        if (item.id === selectedItemId) {
          return item;
        }
        if ((item as any).subItems) {
          for (const subItem of (item as any).subItems) {
            if (subItem.id === selectedItemId) {
              return subItem;
            }
          }
        }
      }
    }
    return macroCategories[2].items[2]; // 기본값: 두 줄로 나누기
  };

  // macroCategories를 기반으로 한 항목 속성 확인 함수들
  const getItemProperty = (itemId: string, property: string) => {
    for (const category of macroCategories) {
      for (const item of category.items) {
        if (item.id === itemId) {
          return (item as any)[property];
        }
        if ((item as any).subItems) {
          for (const subItem of (item as any).subItems) {
            if (subItem.id === itemId) {
              return (subItem as any)[property];
            }
          }
        }
      }
    }
    return false;
  };

  const requiresFile = (itemId: string) =>
    getItemProperty(itemId, "requiresFile");
  const requiresPreset = (itemId: string) =>
    getItemProperty(itemId, "requiresPreset");
  const isFormInput = (itemId: string) =>
    getItemProperty(itemId, "isFormInput");
  // 무료 태그가 있는 항목인지 확인하는 함수
  const isFreeItem = (itemId: string) => {
    return getItemProperty(itemId, "tag") === "무료";
  };

  // ── 이용권 잠금 표시 ──────────────────────────────────────────────
  // 변경/준공 패키지(EXCEL_X2·FLUCTUATION·REPORT)만 있는 사용자가 기본 선택된 계약내역서에
  // 파일을 올리고 실행하다 "엑셀올인원 패키지가 필요합니다"에 막히는 문의가 반복됐다.
  // 화면에 들어올 때 이용권을 한 번 읽어 두고, 쓸 수 있는 카테고리를 앞으로 보내고
  // 계약/기성처럼 살 수 없는 항목은 "구매 필요"로 잠근다. 실행 시점의 이용권 검사는
  // 그대로 남아 있으므로 최종 방어선은 바뀌지 않는다. 조회에 실패하면 예전 화면 그대로다.
  const [activeExcelPermissions, setActiveExcelPermissions] = useState<
    Set<string> | null
  >(null);

  useEffect(() => {
    if (!util.isLogin()) return;
    let cancelled = false;
    api
      .getMyInfo()
      .then((res) => {
        if (cancelled) return;
        const now = new Date();
        const names = new Set<string>();
        (res.data.expiry_dates || []).forEach((expiry) => {
          if (expiry.expiry_date && now <= new Date(expiry.expiry_date)) {
            names.add(expiry.name);
          }
        });
        setActiveExcelPermissions(names);
      })
      .catch(() => {
        // 이용권을 못 읽으면 잠금 표시 없이 기존 동작을 따른다.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // 항목을 쓰는 데 필요한 이용권 조합. 바깥 배열은 "이 중 하나", 안쪽 배열은 "전부 필요".
  // 실행 시점 검사(handleExecute)와 같은 규칙이어야 한다.
  const getRequiredPermissionSets = (itemId: string): string[][] => {
    if (isFreeItem(itemId)) return [];
    if (itemId === "change-7") return [["EXCEL_FLUCTUATION", "EXCEL_REPORT"]];
    const mapped = servicePermissionMapper[itemId];
    if (mapped) {
      return Array.isArray(mapped) ? mapped.map((p) => [p]) : [[mapped]];
    }
    const categoryIndex = getItemProperty(itemId, "originalIndex");
    const permission =
      categoryIndex === null || categoryIndex === false
        ? undefined
        : categoryPermissionMapper[categoryIndex];
    if (!permission || permission === "free") return [];
    return [[permission]];
  };

  const isItemLocked = (itemId: string): boolean => {
    if (!activeExcelPermissions) return false;
    const subItems = getItemProperty(itemId, "subItems");
    if (Array.isArray(subItems) && subItems.length > 0) {
      return subItems.every((sub: any) => isItemLocked(sub.id));
    }
    const sets = getRequiredPermissionSets(itemId);
    if (sets.length === 0) return false;
    return !sets.some((set) =>
      set.every((p) => activeExcelPermissions.has(p))
    );
  };

  const isCategoryLocked = (category: any): boolean =>
    category.items.length > 0 &&
    category.items.every((item: any) => isItemLocked(item.id));

  const hasAnyExcelPermission =
    !!activeExcelPermissions &&
    Array.from(activeExcelPermissions).some((n) => n.startsWith("EXCEL_"));

  // 쓸 수 있는 카테고리를 앞으로, 구매가 필요한 카테고리를 뒤로.
  // 엑셀 이용권이 하나도 없으면(체험·미구매) 원래 순서를 유지한다.
  const orderedCategories = hasAnyExcelPermission
    ? [
        ...macroCategories.filter((c) => !isCategoryLocked(c)),
        ...macroCategories.filter((c) => isCategoryLocked(c)),
      ]
    : macroCategories;

  const lockedItemMessage = (itemId: string) => {
    const needed = getRequiredPermissionSets(itemId).flat();
    const allInOneOnly =
      needed.includes("EXCEL_CONTRACT") || needed.includes("EXCEL_READY");
    return allInOneOnly
      ? "엑셀올인원 패키지 구매가 필요한 기능입니다.\n이용권 페이지로 이동합니다."
      : "엑셀올인원 또는 변경준공 패키지가 필요합니다.\n이용권 페이지로 이동합니다.";
  };

  const goToPaymentForLockedItem = (itemId: string) => {
    logEvent("paywall_shown", "excel_locked_item", { item_id: itemId });
    alertContext.showAlert(lockedItemMessage(itemId), () => {
      history.push(pages.payment);
    });
  };

  // 이용권을 읽은 뒤 기본 선택(계약/착공내역서)이 잠겨 있으면 쓸 수 있는 첫 항목으로 옮긴다.
  useEffect(() => {
    if (!activeExcelPermissions || !hasAnyExcelPermission) return;
    if (fileInfos.length > 0 || !isItemLocked(selectedItemId)) return;
    for (const category of orderedCategories) {
      for (const item of category.items) {
        const leaves = (item as any).subItems ? (item as any).subItems : [item];
        const usable = leaves.find((leaf: any) => !isItemLocked(leaf.id));
        if (usable) {
          setSelectedItemId(usable.id);
          return;
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeExcelPermissions]);

  // 주소(?item=)로 들어온 작업 ID 가 실제 선택 가능한 항목인지 확인하고, 아니면 기본값으로
  useEffect(() => {
    const isLeafItem =
      !!getItemProperty(selectedItemId, "id") &&
      !getItemProperty(selectedItemId, "subItems");
    if (!isLeafItem) {
      setSelectedItemId(DEFAULT_EXCEL_ITEM_ID);
      return;
    }
    const originalIndex = getItemProperty(selectedItemId, "originalIndex");
    if (originalIndex !== null && originalIndex !== undefined) {
      setSelectedCategoryIndex(originalIndex);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 선택한 작업을 주소에 담아 새로고침·링크 공유 후에도 같은 작업이 열리게 한다
  useEffect(() => {
    const params = new URLSearchParams(history.location.search);
    if (params.get(EXCEL_ITEM_QUERY_KEY) === selectedItemId) return;
    params.set(EXCEL_ITEM_QUERY_KEY, selectedItemId);
    history.replace({
      pathname: history.location.pathname,
      search: `?${params.toString()}`,
      state: history.location.state,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItemId, routerLocation.search]);

  // 항목 선택 핸들러
  const handleItemSelect = (itemId: string) => {
    if (isItemLocked(itemId)) {
      goToPaymentForLockedItem(itemId);
      return;
    }
    const previousItemId = selectedItemId;
    setSelectedItemId(itemId);
    
    // 선택된 항목 찾기
    let selectedItem: any = null;
    for (const category of macroCategories) {
      for (const item of category.items) {
        if (item.id === itemId) {
          selectedItem = item;
          break;
        }
        if ((item as any).subItems) {
          for (const subItem of (item as any).subItems) {
            if (subItem.id === itemId) {
              selectedItem = subItem;
              break;
            }
          }
          if (selectedItem) break;
        }
      }
      if (selectedItem) break;
    }

    if (
      selectedItem?.originalIndex !== null &&
      selectedItem?.originalIndex !== undefined
    ) {
      setSelectedCategoryIndex(selectedItem.originalIndex);
    }

    // 서비스 변경 시 시트 초기화 및 자동선택 로직
    if (fileInfos.length > 0) {
      const newItemRequiresFile = selectedItem && selectedItem.requiresFile;
      const oldItemRequiresFile = requiresFile(previousItemId);
      
      if (!newItemRequiresFile) {
        // 새 서비스가 파일 불필요면 파일 전체 초기화 (기존 로직)
        setFileInfos([]);
        setExpandedFiles(new Set());
        fileSizeSum.current = 0;
        sheetCellSum.current = 0;
      } else if (oldItemRequiresFile && newItemRequiresFile && isFileRequiredService(itemId)) {
        // 둘 다 파일 필요 서비스면 시트만 초기화 후 자동선택
        const clearedFileInfos = clearAllSheetSelections(fileInfos);
        const { updatedFileInfos: autoSelectedFiles, selectionResult } = autoSelectSheets(
          clearedFileInfos, 
          itemId as ServiceType
        );
        
        // 선택된 시트들의 셀 수 업데이트
        sheetCellSum.current = calculateSelectedSheetCells(autoSelectedFiles);
        
        // 자동선택 결과 알림
        const notification = generateNotificationMessage(itemId as ServiceType, selectionResult);
        alertContext.showAlert(notification.message);
        
        // 디버그 로그 (개발 환경에서만)
        logAutoSelectionProcess(itemId as ServiceType, autoSelectedFiles, selectionResult);
        
        setFileInfos(autoSelectedFiles);
      } else if (oldItemRequiresFile && newItemRequiresFile && !isFileRequiredService(itemId)) {
        // 새 서비스가 자동선택 대상이 아닌 파일 필요 서비스면 시트만 초기화
        const clearedFileInfos = clearAllSheetSelections(fileInfos);
        sheetCellSum.current = 0;
        setFileInfos(clearedFileInfos);
      }
    }

    // 계약/착공내역서 선택 시 기본 프리셋 자동 적용
    if (itemId === "start-1") {
      const storedDefaultPresets = getDefaultPresetsFromStorage();
      const matchedPresets = {
        fieldRepresentative: presets.find(
          (p) => p.id === storedDefaultPresets.fieldRepresentative?.id
        ),
        companyInfo: presets.find(
          (p) => p.id === storedDefaultPresets.companyInfo?.id
        ),
        projectInfo: presets.find(
          (p) => p.id === storedDefaultPresets.projectInfo?.id
        ),
      };
      setSelectedPresets(matchedPresets);
    } else {
      // 다른 항목 선택 시 선택된 프리셋 초기화
      setSelectedPresets({});
    }

    // 갭지 옵션 초기화
    setGapjiOption("없음");
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // set dragging over style
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    handleFiles(Array.from(e.dataTransfer.files));
  };

  const handleChangeFiles = async (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    const input = e.target;
    if (input.files) {
      if (input.files?.length === 0) return;

      const fileList = Array.from(input.files);
      // 같은 파일을 삭제 후 재선택 시 onChange가 다시 발화되도록 value 초기화
      input.value = '';
      handleFiles(fileList);
    }
  };

  const handleFiles = async (files: File[]) => {
    // 구매가 필요한 항목이 선택된 상태에서 파일을 올리면 이용권 페이지로 보낸다.
    if (isItemLocked(selectedItemId)) {
      goToPaymentForLockedItem(selectedItemId);
      return;
    }
    const uploadedfiles = [];
    const infos: ExcelX2File[] = [];
    // 파일의 확장자가 xlsx가 아니라면 경고 후 xlsx인 파일만 필터

    if (files.some((file) => file.name.split(".").pop() !== "xlsx")) {
      alertContext.showAlert("엑셀(.xlsx) 파일만 업로드할 수 있습니다.");
      files = files.filter((file) => file.name.split(".").pop() === "xlsx");
      if (files.length === 0) return;
    }
    for (let i = 0; i < (files.length || 0); i++) {
      let file = files[i];
      fileSizeSum.current += file.size;
      if (
        fileSizeSum.current < fileSizeLimit * megaByte &&
        i + fileInfos?.length < fileCountLimit
      ) {
        // 업로드와 해시 계산을 병렬로 수행 (해시 실패해도 업로드는 진행)
        const [res, fileHash] = await Promise.all([
          api.uploadFileAsync(file, "excelX2"),
          computeFileSha256(file).catch(() => undefined),
        ]);
        uploadedfiles.push(res.data.url);
        // get file extension

        infos.push({
          name: file.name,
          url: res.data.url,
          size: file.size,
          isEntireChecked: false,
          fileHash,
        });
      } else {
        alertContext.showAlert(
          `파일은 최대 ${fileCountLimit}개, 합계 ${fileSizeLimit}MB를 넘을 수 없습니다.`
        );
        break;
      }
    }
    setFileInfos([...fileInfos, ...infos]);
    getSheetNames(uploadedfiles, infos);
  };

  const getSheetNames = async (files: string[], infos: ExcelX2File[]) => {
    loading.showLoading("엑셀시트 목록을 불러오는 중입니다.");
    try {
      const res = await api.getSheetNamesAsync(files);
      let errorMessage = "";
      const errorFiles: any = [];
      loading.closeLoading();
      if (res.status === 200) {
        infos.forEach((fileInfo, index) => {
          if (res.data.data[fileInfo.url]?.find(Boolean).errorMessage) {
            errorFiles.push({
              name: fileInfo.name,
              errorMessage:
                res.data.data[fileInfo.url]?.find(Boolean).errorMessage,
              index,
            });
          } else {
            // 원본 URL 보존
            const originalUrl = fileInfo.url;
            // convertedUrl이 있는지 확인
            const sheetData = res.data.data[originalUrl];
            const firstSheetWithConvertedUrl = sheetData?.find(
              (sheet: any) => sheet.convertedUrl
            );

            // 시트 데이터 매핑 (원본 URL 사용)
            fileInfo.sheets = res.data.data[originalUrl].map(
              (sheetInfo: {
                sheetName: string;
                isHide: boolean;
                lastRow: number;
                lastColumn: number;
                sheetIndex: number;
                errorMessage: string;
                convertedUrl?: string;
                estimatedSecondsX2?: number | null;
                maxJobSeconds?: number | null;
                formulas?: number | null;
                merges?: number | null;
              }): ExcelX2FileSheet => {
                if (sheetInfo.errorMessage) {
                  errorMessage += sheetInfo.errorMessage + "\n";
                }
                return {
                  name: sheetInfo.sheetName,
                  checked: false,
                  isHidden: sheetInfo.isHide,
                  lastRow: sheetInfo.lastRow,
                  lastColumn: sheetInfo.lastColumn,
                  sheetIndex: sheetInfo.sheetIndex,
                  estimatedSecondsX2: sheetInfo.estimatedSecondsX2,
                  maxJobSeconds: sheetInfo.maxJobSeconds,
                  formulas: sheetInfo.formulas,
                  merges: sheetInfo.merges,
                };
              }
            );

            // convertedUrl이 있다면 파일의 url을 교체 (시트 데이터 처리 후에)
            if (firstSheetWithConvertedUrl?.convertedUrl) {
              fileInfo.url = firstSheetWithConvertedUrl.convertedUrl;
            }
          }
        });
        if (errorFiles.length > 0) {
          errorFiles?.forEach((errorSheet: any) => {
            errorMessage += `${errorSheet.name}: ${errorSheet.errorMessage}\n`;
            infos.splice(errorSheet.index, 1);
          });
          alertContext.showAlert(
            "처리되지 않은 파일이 있습니다.\n아래 사유를 확인해주세요.\n\n" +
              errorMessage
          );
        } else if (errorMessage) {
          alertContext.showAlert(errorMessage);
        }

        // 파일이 성공적으로 업로드되면 자동으로 시트 선택 탭 열기
        const newExpandedFiles = new Set(expandedFiles);
        const currentFileCount = fileInfos.length;
        infos.forEach((_, index) => {
          newExpandedFiles.add(currentFileCount + index);
        });
        setExpandedFiles(newExpandedFiles);

        const updatedFileInfos = [...fileInfos, ...infos];
        
        // 자동 시트 선택 기능 실행
        if (isFileRequiredService(selectedItemId)) {
          const { updatedFileInfos: autoSelectedFiles, selectionResult } = autoSelectSheets(
            updatedFileInfos,
            selectedItemId as ServiceType
          );
          
          // 선택된 시트들의 셀 수 업데이트
          sheetCellSum.current = calculateSelectedSheetCells(autoSelectedFiles);
          
          // 자동선택 결과 알림
          const notification = generateNotificationMessage(selectedItemId as ServiceType, selectionResult);
          alertContext.showAlert(notification.message);
          
          // 디버그 로그 (개발 환경에서만)
          logAutoSelectionProcess(selectedItemId as ServiceType, autoSelectedFiles, selectionResult);
          
          setFileInfos(autoSelectedFiles);
        } else {
          setFileInfos(updatedFileInfos);
        }
      }
    } catch (err: any) {
      loading.closeLoading();
      console.log(err);

      alertContext.showAlert(
        getExcelErrorMessage(
          err,
          "오류가 발생했습니다.\n고객센터에 문의해주세요."
        )
      );
    }
  };

  const handleDelete = (index: number) => (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    const newFileInfos = [...fileInfos];
    newFileInfos[index].sheets?.forEach((sheet) => {
      if (sheet.checked) {
        sheetCellSum.current -= sheet.lastRow * sheet.lastColumn;
      }
    });
    newFileInfos.splice(index, 1);
    setFileInfos(newFileInfos);
    fileSizeSum.current -= fileInfos[index].size;

    // 파일 삭제 시 expandedFiles도 업데이트
    const newExpandedFiles = new Set<number>();
    expandedFiles.forEach((fileIndex) => {
      if (fileIndex < index) {
        newExpandedFiles.add(fileIndex);
      } else if (fileIndex > index) {
        newExpandedFiles.add(fileIndex - 1);
      }
      // fileIndex === index인 경우는 제외 (삭제된 파일)
    });
    setExpandedFiles(newExpandedFiles);
  };

  const handleOnOriginChange = (value: string) => {
    setOriginOption(value);
  };

  const handleOnOriginRowChange = (value: boolean) => {
    setIsOriginUpper(value);
  };

  const handleOnDivideChange = (value: string) => {
    setDivideOption(value);
  };

  const handleOnGapjiChange = (value: string) => {
    setGapjiOption(value);
  };

  const handleOnDivideDirectionChange = (value: string) => {
    setDivideDirectionOption(value);
  };

  const handleAdvancePaymentChange = (value: string) => {
    setAdvancePayment(value);
  };
  const handleCompletionSettlementAmountChange = (value: string) => {
    setCompletionSettlementAmount(value);
  };

  const handleClaimDateChange = (value: string) => {
    setClaimDate(value);
  };

  const handleClaimAmountChange = (value: string) => {
    setClaimAmount(value);
  };

  // 선급금 신청서 핸들러들

  const handleContractMaterialCostChange = (value: string) => {
    setContractMaterialCost(value);
  };

  const handleContractIndirectLaborCostChange = (value: string) => {
    setContractIndirectLaborCost(value);
  };

  const handleContractExpensesChange = (value: string) => {
    setContractExpenses(value);
  };

  const handleContractOtherChange = (value: string) => {
    setContractOther(value);
  };

  const handlePrepaymentAdjustmentMaterialCostChange = (value: string) => {
    setPrepaymentAdjustmentMaterialCost(value);
  };

  const handlePrepaymentAdjustmentIndirectLaborCostChange = (value: string) => {
    setPrepaymentAdjustmentIndirectLaborCost(value);
  };

  const handlePrepaymentExpensesChange = (value: string) => {
    setPrepaymentExpenses(value);
  };

  const handlePrepaymentOtherChange = (value: string) => {
    setPrepaymentOther(value);
  };

  const handleScheduleDataChange = useCallback((data: any) => {
    setScheduleData(data);
  }, []);

  const handleReestimationSheetChange = (value: boolean) => {
    setIncludeReestimationSheet(value);
  };

  // 차수분 내역서 관련 핸들러들
  const handlePhaseCountChange = (value: number) => {
    setPhaseCount(value);
  };

  const handlePhaseTextsChange = (value: string[]) => {
    setPhaseTexts(value);
  };

  const handleTotalTextChange = (value: string) => {
    setTotalText(value);
  };

  const handleRemainderTextChange = (value: string) => {
    setRemainderText(value);
  };

  const handleUseExistingFilesChange = (value: boolean) => {
    setUseExistingFiles(value);
  };

  const handlePhaseFileChange = async (e: React.ChangeEvent<HTMLInputElement>, phase: number) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const existingIndex = phaseFiles.findIndex(pf => pf.phase === phase);
    const newPhaseFile = { phase, file };

    if (existingIndex >= 0) {
      setPhaseFiles(prev => [
        ...prev.slice(0, existingIndex),
        newPhaseFile,
        ...prev.slice(existingIndex + 1)
      ]);
    } else {
      setPhaseFiles(prev => [...prev, newPhaseFile]);
    }

    await fetchPhaseSheetInfo(file, phase);
  };

  const handleDeletePhaseFile = (phase: number) => {
    setPhaseFiles(prev => prev.filter(pf => pf.phase !== phase));
    if (phaseFileInputRefs.current[phase]) {
      phaseFileInputRefs.current[phase]!.value = "";
    }
  };

  const handlePhaseSheetToggle = (sheetIndex: number, phaseIndex: number) => {
    setPhaseFiles(prev => prev.map((pf, idx) => {
      if (idx === phaseIndex) {
        const selectedSheets = pf.selectedSheets || [];
        const newSelectedSheets = selectedSheets.includes(sheetIndex)
          ? selectedSheets.filter((s: number) => s !== sheetIndex)
          : [...selectedSheets, sheetIndex];
        return { ...pf, selectedSheets: newSelectedSheets };
      }
      return pf;
    }));
  };

  const handleSelectAllPhaseSheets = (phaseIndex: number) => {
    const phaseFile = phaseFiles[phaseIndex];
    if (phaseFile && phaseFile.sheets) {
      const allSelected = phaseFile.selectedSheets?.length === phaseFile.sheets.length;
      setPhaseFiles(prev => prev.map((pf, idx) => {
        if (idx === phaseIndex) {
          return {
            ...pf,
            selectedSheets: allSelected ? [] : pf.sheets!.map(s => s.index),
          };
        }
        return pf;
      }));
    }
  };

  // 파일에서 시트 정보 가져오기
  const fetchPhaseSheetInfo = async (file: File, phase: number) => {
    const phaseIndex = phaseFiles.findIndex(pf => pf.phase === phase);
    if (phaseIndex === -1) return;
    
    setPhaseFiles(prev => prev.map((pf, idx) => 
      idx === phaseIndex ? { ...pf, isLoading: true } : pf
    ));

    try {
      const formData = new FormData();
      formData.append("file", file);

      // 파일 업로드 - 기존 API 사용
      const uploadResponse = await api.uploadFileAsync(file, "excelX2");
      const s3Url = uploadResponse.data.url;
      
      // 기본 시트 정보 설정 (실제 시트 정보는 서버에서 처리)
      const sheets = [
        {
          name: "Sheet1",
          checked: true,
          sheetIndex: 0,
          lastRow: 100,
          lastColumn: 10,
          isHidden: false
        }
      ];

      setPhaseFiles(prev => prev.map((pf, idx) => 
        idx === phaseIndex ? { 
          ...pf, 
          isLoading: false 
        } : pf
      ));
    } catch (error) {
      setPhaseFiles(prev => prev.map((pf, idx) => 
        idx === phaseIndex ? { ...pf, isLoading: false } : pf
      ));
      alertContext.showAlert("파일 정보를 가져오는데 실패했습니다.");
    }
  };

  // 차수분 내역서 실행 핸들러 (Presenter에서 호출용)
  const handlePhasedStatementExecutionFromPresenter = async () => {
    // 로그인 체크
    if (!util.isLogin()) {
      alertContext.showAlert("로그인이 필요합니다.", () => {
        history.replace(pages.login);
      });
      return;
    }

    // 이용권 체크 (EXCEL_X2 필요)
    const res = await api.getMyInfo();
    const hasPermission = res.data.expiry_dates.find((expiry) => {
      if (expiry.name === "EXCEL_X2") {
        if (expiry.expiry_date) {
          const now = new Date();
          const expiryDate = new Date(expiry.expiry_date);
          return now <= expiryDate;
        }
        return false;
      }
      return false;
    });

    if (!hasPermission) {
      logEvent("paywall_shown", "excel_phased_statement");
      alertContext.showAlert(
        "엑셀올인원 또는 변경준공 패키지가 필요합니다."
      );
      history.push(pages.payment);
      return;
    }

    try {
      setIsProcessing(true);

      // 유효성 검사
      if (!fileInfos || fileInfos.length === 0) {
        alertContext.showAlert("파일을 먼저 업로드해주세요.");
        return;
      }
      
      const selectedSheets = fileInfos[0]?.sheets?.filter(s => s.checked)?.map(s => s.sheetIndex) || [];
      if (selectedSheets.length === 0) {
        alertContext.showAlert("최소 하나 이상의 시트를 선택해주세요.");
        return;
      }
      
      // 1차가 아닌 경우에만 차수 파일 검증
      if (useExistingFiles && phaseCount > 1) {
        if (phaseFiles.length === 0) {
          alertContext.showAlert("최소 하나 이상의 차수 파일을 업로드해주세요.");
          return;
        }
        
        const missingPhases = [];
        for (let i = 0; i < phaseCount - 1; i++) {
          if (!phaseFiles.find(pf => pf.phase === i + 1)) {
            missingPhases.push(i + 1);
          }
        }
        
        if (missingPhases.length > 0) {
          alertContext.showAlert(`${missingPhases.join(", ")}차 파일이 누락되었습니다.`);
          return;
        }
        
        // 차수별 파일의 시트 선택 확인은 제거 - 전체분에서 선택한 시트를 사용
      }
      
      // 차수분 내역서 처리
      const allPhaseTexts = [totalText, ...phaseTexts, remainderText];
      const data = {
        phaseCount,
        phaseTexts: allPhaseTexts,
        totalFile: {
          url: fileInfos[0].url,
          selectedSheets: selectedSheets,
        },
        phaseFiles: useExistingFiles ? phaseFiles.map(pf => ({
          phase: pf.phase,
          file: pf.file,
          selectedSheets: selectedSheets,  // 전체분에서 선택한 시트를 동일하게 적용
        })) : [],
      };
      
      await handlePhasedStatementExecution(data);
      // 성공적으로 완료된 경우 추가 작업 없음
    } catch (error) {
      console.error('차수분 내역서 실행 중 오류:', error);
      // handlePhasedStatementExecution 내부에서 이미 오류 처리를 하므로
      // 여기서는 추가 메시지를 표시하지 않음
    } finally {
      setIsProcessing(false);
    }
  };

  const handleEntireCheck = (index: number) => {
    if (fileInfos[index].isEntireChecked) {
      const newFileInfos = [...fileInfos];
      newFileInfos[index].isEntireChecked = false;
      newFileInfos[index].sheets = newFileInfos[index].sheets?.map((sheet) => {
        if (sheet.checked) {
          sheetCellSum.current -= sheet.lastRow * sheet.lastColumn;
        }
        sheet.checked = false;
        return sheet;
      });

      setFileInfos(newFileInfos);
    } else {
      const newFileInfos = [...fileInfos];
      newFileInfos[index].isEntireChecked = true;
      newFileInfos[index].sheets = newFileInfos[index].sheets?.map((sheet) => {
        if (!isHiddenSheetShow && sheet.isHidden) return sheet;
        if (!sheet.checked) {
          sheetCellSum.current += sheet.lastRow * sheet.lastColumn;
        }
        sheet.checked = true;
        return sheet;
      });

      setFileInfos(newFileInfos);
    }
  };

  /** 예상 시간이 한도를 넘을 때 안내 버튼: 통합문서 시트(일위대가·단가산출·수량산출 등) 선택을 한 번에 푼다 */
  const handleUncheckNonStatementSheets = () => {
    const newFileInfos = [...fileInfos];
    newFileInfos.forEach((f) => {
      (f.sheets || []).forEach((sh) => {
        if (sh.checked && NON_STATEMENT_SHEET.test(sh.name)) {
          sh.checked = false;
          sheetCellSum.current -= sh.lastRow * sh.lastColumn;
        }
      });
      f.isEntireChecked = (f.sheets || []).length > 0 && (f.sheets || []).every((sh) => sh.checked);
    });
    setFileInfos(newFileInfos);
  };

  /** 한도의 80% 안에 드는 앞쪽 시트만 남기고 나머지 선택 해제(1번째 묶음). 다음 묶음은 같은 파일을 다시 올려 고른다. */
  const handleKeepSheetsWithinLimit = () => {
    const newFileInfos = [...fileInfos];
    const before = newFileInfos.flatMap((f) => (f.sheets || []).filter((sh) => sh.checked));
    const r = keepSheetsWithinLimit(newFileInfos);
    before.forEach((sh) => { if (!sh.checked) sheetCellSum.current -= sh.lastRow * sh.lastColumn; });
    setFileInfos(newFileInfos);
    if (r.unchecked > 0) alertContext.showAlert(`앞쪽 ${r.kept}개 시트만 남겼습니다. 이 작업이 끝나면 같은 파일을 다시 올려 나머지 ${r.unchecked}개 시트를 선택해 실행해 주세요.`);
  };

  const handleSheetClick = (fileIndex: number, sheet: ExcelX2FileSheet) => {
    const newFileInfos = [...fileInfos];
    const checked = sheet.checked;

    if (newFileInfos[fileIndex].sheets?.length ?? 0 > 0) {
      sheet.checked = !checked;
      if (checked) {
        newFileInfos[fileIndex].isEntireChecked = false;
        sheetCellSum.current -= sheet.lastRow * sheet.lastColumn;
      } else {
        sheetCellSum.current += sheet.lastRow * sheet.lastColumn;
      }
    }

    newFileInfos[fileIndex].isEntireChecked = (
      newFileInfos[fileIndex].sheets ?? []
    ).every((sheet) =>
      isHiddenSheetShow ? sheet.checked : sheet.isHidden ? true : sheet.checked
    );

    setFileInfos(newFileInfos);
  };

  const handleExecute = async () => {
    await handleUnifiedExecution(selectedItemId);
  };

  // 통합된 실행 함수 - ID 기반으로 파일 처리와 기본정보 기반 서류 생성을 모두 처리
  const handleUnifiedExecution = async (
    itemId: string,
    additionalData?: any
  ) => {
    if (!util.isLogin()) {
      alertContext.showAlert("로그인이 필요합니다.", () => {
        history.replace(pages.login);
      });
      return;
    }

    // 파일이 필요한 항목인지 확인
    const needsFile = requiresFile(itemId);
    // 프리셋이 필요한 항목인지 확인
    const needsPreset = requiresPreset(itemId);

    // 프리셋 검증
    if (needsPreset) {
      const storedDefaults = getDefaultPresetsFromStorage();
      const hasCompanyInfo = !!(
        selectedPresets.companyInfo ||
        presets.find((p) => p.id === storedDefaults?.companyInfo?.id)
      );
      const hasFieldRep = !!(
        selectedPresets.fieldRepresentative ||
        presets.find((p) => p.id === storedDefaults?.fieldRepresentative?.id)
      );
      const hasProjectInfo = !!(
        selectedPresets.projectInfo ||
        presets.find((p) => p.id === storedDefaults?.projectInfo?.id)
      );

      if (!hasCompanyInfo || !hasFieldRep || !hasProjectInfo) {
        alertContext.showAlert("기본 정보를 모두 설정해주세요.");
        return;
      }
    }

    // 이용권 확인 - 무료 태그가 있는 항목은 이용권 확인 건너뛰기
    if (!isFreeItem(itemId)) {
      const res = await api.getMyInfo();

      // 증감 + 실정보고서(change-7)는 두 개의 이용권이 모두 필요
      if (itemId === "change-7") {
        const fluctuationExpire = res.data.expiry_dates.find((expiry) => {
          if (expiry.name === "EXCEL_FLUCTUATION") {
            if (expiry.expiry_date) {
              const now = new Date();
              const expiryDate: any = new Date(expiry.expiry_date);
              if (now > expiryDate) {
                return null;
              }
              return true;
            } else {
              return null;
            }
          }
          return null;
        });

        const reportExpire = res.data.expiry_dates.find((expiry) => {
          if (expiry.name === "EXCEL_REPORT") {
            if (expiry.expiry_date) {
              const now = new Date();
              const expiryDate: any = new Date(expiry.expiry_date);
              if (now > expiryDate) {
                return null;
              }
              return true;
            } else {
              return null;
            }
          }
          return null;
        });

        if (!fluctuationExpire || !reportExpire) {
          logEvent("paywall_shown", "excel_fluctuation_report", { item_id: itemId });
          alertContext.showAlert(
            "엑셀올인원 또는 변경준공 패키지가 필요합니다."
          );
          history.push(pages.payment);
          return;
        }
      } else if (servicePermissionMapper[itemId]) {
        // 서비스 ID별 권한 매핑 확인
        const mappedPermission = servicePermissionMapper[itemId];

        // 배열인 경우 (둘 중 하나만 있으면 됨 - OR 조건)
        if (Array.isArray(mappedPermission)) {
          const hasAnyPermission = mappedPermission.some((permName) => {
            return res.data.expiry_dates.find((expiry) => {
              if (expiry.name === permName) {
                if (expiry.expiry_date) {
                  const now = new Date();
                  const expiryDate = new Date(expiry.expiry_date);
                  return now <= expiryDate;
                }
                return false;
              }
              return false;
            });
          });

          if (!hasAnyPermission) {
            logEvent("paywall_shown", "excel_service_any", { item_id: itemId });
            alertContext.showAlert(
              "엑셀올인원 또는 변경준공 패키지가 필요합니다."
            );
            history.push(pages.payment);
            return;
          }
        } else {
          // 단일 권한 체크
          const hasPermission = res.data.expiry_dates.find((expiry) => {
            if (expiry.name === mappedPermission) {
              if (expiry.expiry_date) {
                const now = new Date();
                const expiryDate = new Date(expiry.expiry_date);
                return now <= expiryDate;
              }
              return false;
            }
            return false;
          });

          if (!hasPermission) {
            // EXCEL_CONTRACT는 엑셀올인원만, EXCEL_X2는 둘 다 가능
            const message =
              mappedPermission === "EXCEL_CONTRACT"
                ? "엑셀올인원 패키지가 필요합니다."
                : "엑셀올인원 또는 변경준공 패키지가 필요합니다.";
            logEvent("paywall_shown", "excel_service", { item_id: itemId, permission: mappedPermission });
            alertContext.showAlert(message);
            history.push(pages.payment);
            return;
          }
        }
      } else if (itemId === "completion-1") {
        // 준공내역서(completion-1)는 EXCEL_X2 필요
        const x2Expire = res.data.expiry_dates.find((expiry) => {
          if (expiry.name === "EXCEL_X2") {
            if (expiry.expiry_date) {
              const now = new Date();
              const expiryDate: any = new Date(expiry.expiry_date);
              if (now > expiryDate) {
                return null;
              }
              return true;
            } else {
              return null;
            }
          }
          return null;
        });

        if (!x2Expire) {
          logEvent("paywall_shown", "excel_x2", { item_id: itemId });
          alertContext.showAlert(
            "엑셀올인원 또는 변경준공 패키지가 필요합니다."
          );
          history.push(pages.payment);
          return;
        }
      } else {
        // 일반 항목들의 이용권 확인
        const categoryIndex = getItemProperty(itemId, "originalIndex");

        const expire = res.data.expiry_dates.find((expiry) => {
          if (categoryPermissionMapper[categoryIndex] === "free") {
            return true;
          }
          if (categoryPermissionMapper[categoryIndex] === expiry.name) {
            if (expiry.expiry_date) {
              const now = new Date();
              const expiryDate: any = new Date(expiry.expiry_date);
              if (now > expiryDate) {
                return null;
              }
              return true;
            } else {
              return null;
            }
          }
          return null;
        });

        if (!expire) {
          // 계약/기성은 엑셀올인원만, 변경/준공 관련은 둘 다 가능
          const permissionType = categoryPermissionMapper[categoryIndex];
          const message =
            permissionType === "EXCEL_CONTRACT" ||
            permissionType === "EXCEL_READY"
              ? "엑셀올인원 패키지가 필요합니다."
              : "엑셀올인원 또는 변경준공 패키지가 필요합니다.";
          logEvent("paywall_shown", "excel_category", { item_id: itemId, permission: permissionType });
          alertContext.showAlert(message);
          history.push(pages.payment);
          return;
        }
      }
    }

    // 프리셋 데이터 준비
    const storedDefaultPresets = getDefaultPresetsFromStorage();
    const rawPresets = {
      fieldRepresentative:
        selectedPresets.fieldRepresentative?.data ||
        presets.find(
          (p) => p.id === storedDefaultPresets.fieldRepresentative?.id
        )?.data ||
        null,
      companyInfo:
        selectedPresets.companyInfo?.data ||
        presets.find((p) => p.id === storedDefaultPresets.companyInfo?.id)
          ?.data ||
        null,
      projectInfo:
        selectedPresets.projectInfo?.data ||
        presets.find((p) => p.id === storedDefaultPresets.projectInfo?.id)
          ?.data ||
        null,
    };

    // 백엔드 전송용 프리셋 데이터 (이름 필드에 공백 처리)
    const formattedPresets = {
      fieldRepresentative: rawPresets.fieldRepresentative
        ? {
            ...rawPresets.fieldRepresentative,
            name: (rawPresets.fieldRepresentative as any).name
              ? (rawPresets.fieldRepresentative as any).name
                  .replace(/\s+/g, "")
                  .split("")
                  .join("  ")
              : (rawPresets.fieldRepresentative as any).name,
          }
        : null,
      companyInfo: rawPresets.companyInfo
        ? {
            ...rawPresets.companyInfo,
            representative: (rawPresets.companyInfo as any).representative
              ? (rawPresets.companyInfo as any).representative
                  .replace(/\s+/g, "")
                  .split("")
                  .join("  ")
              : (rawPresets.companyInfo as any).representative,
          }
        : null,
      projectInfo: rawPresets.projectInfo
        ? {
            ...rawPresets.projectInfo,
            client: (rawPresets.projectInfo as any).client
              ? (() => {
                  const clientValue = (
                    rawPresets.projectInfo as any
                  ).client.trim();
                  return clientValue && !clientValue.endsWith("귀하")
                    ? clientValue + " 귀하"
                    : clientValue;
                })()
              : (rawPresets.projectInfo as any).client,
          }
        : null,
    };

    if (needsFile) {
      // 파일 처리가 필요한 항목들 (기존 엑셀 가공 작업)
      await handleFileBasedExecution(itemId, formattedPresets);
    } else {
      // 기본정보 기반 서류 생성
      await handlePresetBasedExecution(
        itemId,
        formattedPresets,
        additionalData
      );
    }
  };

  // 파일 기반 엑셀 가공 실행
  const handleFileBasedExecution = async (itemId: string, presets: any) => {
    if (fileInfos.length === 0) {
      alertContext.showAlert("파일을 업로드해주세요.");
      return;
    }

    const data: {
      s3Url: string;
      selectedSheets: number[];
      fileHash?: string;
    }[] = [];

    for (const fileInfo of fileInfos) {
      if (fileInfo.sheets?.every((sheet) => !sheet.checked)) {
        alertContext.showAlert("시트를 선택해주세요.");
        return;
      }

      const selectedSheets: number[] = [];
      fileInfo.sheets?.forEach((sheet) => {
        if (sheet.checked) {
          selectedSheets.push(sheet.sheetIndex);
        }
      });

      data.push({
        s3Url: fileInfo.url,
        selectedSheets,
        fileHash: fileInfo.fileHash,
      });
    }

    try {
      const requests = getRequestsByItemId(itemId, data, presets);
      const responses = await Promise.all(requests);

      // 비동기 응답: tasks 배열 수집
      const allTasks: { task_id: number; file_name: string }[] = [];
      responses.forEach((res) => {
        if (res.status === 200 && res.data.tasks) {
          allTasks.push(...res.data.tasks);
        }
      });

      if (allTasks.length > 0) {
        addExcelTasks(allTasks);
        alertContext.showAlert(
          "처리가 시작되었습니다.\n우측 처리내역 패널에서 진행 상태를 확인하세요.\n완료 시 자동으로 다운로드됩니다."
        );
        sheetCellSum.current = 0;
        setFileInfos([]);
      } else {
        alertContext.showAlert("처리 요청에 실패했습니다.\n고객센터에 문의해주세요.");
      }
    } catch (err: any) {
      console.log(err);
      if (err?.response?.status === 429) {
        alertContext.showAlert(err.response.data?.message || "동시 처리 가능한 작업 수를 초과했습니다.\n진행중인 작업이 완료된 후 다시 시도해주세요.");
      } else if (err?.response?.status === 409) {
        alertContext.showAlert(err.response.data?.message || "동일 파일이 이미 처리 중입니다.\n처리가 끝난 뒤 다시 시도해주세요.");
      } else {
        alertContext.showAlert(
          getExcelErrorMessage(
            err,
            "오류가 발생했습니다.\n고객센터에 문의해주세요."
          )
        );
      }
    }
  };

  // 차수분 내역서 처리 함수
  const handlePhasedStatementExecution = async (phasedData: any) => {
    if (!phasedData) {
      alertContext.showAlert("차수분 내역서 설정을 완료해주세요.");
      return;
    }

    // 비동기 처리로 전환 - 로딩 UI 대신 처리내역 패널에서 상태 추적

    try {
      // JSON 데이터 준비
      const requestData: any = {
        data: [{
          s3Url: phasedData.totalFile.url,
          selectedSheets: phasedData.totalFile.selectedSheets,
          fileHash: phasedData.totalFile.fileHash,
        }],
        phaseCount: phasedData.phaseCount,
        phaseTexts: phasedData.phaseTexts,
        phaseFiles: []
      };

      // 차수별 파일 정보 추가 (기존 파일이 있는 경우에만)
      if (phasedData.phaseFiles && phasedData.phaseFiles.length > 0) {
        for (const phaseFile of phasedData.phaseFiles) {
          // 파일 업로드 먼저
          const uploadResponse = await api.uploadFileAsync(phaseFile.file);
          const s3Url = uploadResponse.data.url;
          
          requestData.phaseFiles.push({
            phase: phaseFile.phase,
            s3Url: s3Url,
            selectedSheets: phaseFile.selectedSheets
          });
        }
      }

      // API 호출 (비동기)
      const response = await api.postExcelPhasedStatementExecute(requestData);

      if (response.status >= 200 && response.status < 300 && response.data.tasks) {
        addExcelTasks(response.data.tasks);
        alertContext.showAlert(
          "차수분 내역서 처리가 시작되었습니다.\n우측 처리내역 패널에서 진행 상태를 확인하세요."
        );

        // 파일 목록 초기화
        // (setUploadFiles 는 선언된 적 없는 이름이라 여기서 ReferenceError 가 나서, 작업은 접수됐는데
        //  "차수분 내역서 생성 중 오류가 발생했습니다" 알림이 떴다 — 10-14 김호영 문의)
        setFileInfos([]);
        sheetCellSum.current = 0;
        setPhaseCount(2);
        setPhaseTexts(["1차", "2차"]);
        setTotalText("전체");
        setRemainderText("잔여");
        setUseExistingFiles(false);
        setPhaseFiles([]);
        setIsProcessing(false);
      } else {
        alertContext.showAlert(
          "차수분 내역서 처리 요청에 실패했습니다.\n고객센터에 문의해주세요."
        );
      }
    } catch (error: any) {
      console.error("차수분 내역서 생성 오류:", error);

      if (error?.response?.status === 429) {
        alertContext.showAlert(error.response.data?.message || "동시 처리 가능한 작업 수를 초과했습니다.");
      } else if (error?.response?.status === 409) {
        alertContext.showAlert(error.response.data?.message || "동일 파일이 이미 처리 중입니다.\n처리가 끝난 뒤 다시 시도해주세요.");
      } else if (isExcelOverloadError(error)) {
        // 처리 시간 초과인지, 서버에 닿지 못한 것인지에 따라 안내가 다르다
        alertContext.showAlert(
          getExcelErrorMessage(
            error,
            "차수분 내역서 생성에 실패했습니다.\n고객센터에 문의해주세요."
          )
        );
      } else if (error.response) {
        alertContext.showAlert(
          `차수분 내역서 생성 중 오류가 발생했습니다.\n상태 코드: ${
            error.response.status
          }\n${error.response.data?.message || "고객센터에 문의해주세요."}`
        );
      } else if (error.request) {
        alertContext.showAlert(
          "차수분 내역서 생성 중 네트워크 오류가 발생했습니다.\n인터넷 연결을 확인해주세요."
        );
      } else {
        alertContext.showAlert(
          "차수분 내역서 생성 중 오류가 발생했습니다.\n고객센터에 문의해주세요."
        );
      }
    }
  };

  // 기본정보 기반 서류 생성 실행
  const handlePresetBasedExecution = async (
    itemId: string,
    presets: any,
    additionalData?: any
  ) => {
    try {
      // ID 기반 docType 매핑
      const getDocTypeByItemId = (itemId: string): string => {
        switch (itemId) {
          case "start-4":
            return "contract";
          case "start-2-1":
            return "construction";
          case "start-2-2":
            return "safetyPlan";
          case "start-2-3":
            return "environmentPlan";
          case "start-2-4":
            return "directConstruction";
          case "completion-2-1":
            return "completion";
          case "completion-2-2":
            return "safetyReport";
          case "completion-2-3":
            return "environmentReport";
          case "billing-1":
            return "billing";
          case "start-3":
            return "prepayment";
          default:
            throw new Error("지원하지 않는 서류 유형입니다.");
        }
      };

      const docType = getDocTypeByItemId(itemId);

      // 기본 페이로드 구성
      const payload: any = {
        docType,
        presets: presets,
        formData: isFormInput(itemId)
          ? itemId.includes("start-2-2") || itemId.includes("completion-2-2")
            ? safetyManagePlan
            : enviManagePlan
          : undefined,
        advancePayment: advancePayment ? advancePayment.replace(/,/g, "") : "",
        completionSettlementAmount: completionSettlementAmount
          ? completionSettlementAmount.replace(/,/g, "")
          : "",
        claimDate: claimDate || "",
        claimAmount: claimAmount ? claimAmount.replace(/,/g, "") : "",
        prepaymentData: {
          contractMaterialCost: contractMaterialCost
            ? contractMaterialCost.replace(/,/g, "")
            : "",
          contractIndirectLaborCost: contractIndirectLaborCost
            ? contractIndirectLaborCost.replace(/,/g, "")
            : "",
          contractExpenses: contractExpenses
            ? contractExpenses.replace(/,/g, "")
            : "",
          contractOther: contractOther ? contractOther.replace(/,/g, "") : "",
          prepaymentAdjustmentMaterialCost: prepaymentAdjustmentMaterialCost
            ? prepaymentAdjustmentMaterialCost.replace(/,/g, "")
            : "",
          prepaymentAdjustmentIndirectLaborCost:
            prepaymentAdjustmentIndirectLaborCost
              ? prepaymentAdjustmentIndirectLaborCost.replace(/,/g, "")
              : "",
          prepaymentExpenses: prepaymentExpenses
            ? prepaymentExpenses.replace(/,/g, "")
            : "",
          prepaymentOther: prepaymentOther
            ? prepaymentOther.replace(/,/g, "")
            : "",
        },
      };

      // 착공계약서류일 때 scheduleData 추가
      if (itemId === "start-2-1" && additionalData) {
        const scheduleItems =
          additionalData.rows?.map((row: any) => ({
            workTypeName: row.workTypeName,
            percentage: row.percentage,
            ratios: row.ratios,
            totalAmount: row.totalAmount,
          })) || [];

        payload.scheduleData = {
          scheduleItems: scheduleItems,
          startDate: additionalData.startDate,
          completionDate: additionalData.completionDate,
          totalWorkDays: additionalData.totalWorkDays,
          subHeaderDates: additionalData.subHeaderDates,
        };
      }

      // additionalData가 있으면 페이로드에 병합
      if (additionalData && itemId !== "start-2-1") {
        Object.assign(payload, additionalData);
        if (additionalData.formData) {
          payload.formData = additionalData.formData;
        }
      }

      // 사용계획 숫자 칸("1,000"·"" 등)을 정리해 보낸다 — 서버가 decimal 로 읽지 못해 400 이 나던 것
      if (payload.formData) {
        payload.formData = normalizeUsagePlanFormData(payload.formData);
      }

      const response = await api.postExcelDocumentExecute(payload);

      if (response.status >= 200 && response.status < 300 && response.data.tasks) {
        addExcelTasks(response.data.tasks);
        alertContext.showAlert(
          "서류 생성이 시작되었습니다.\n우측 처리내역 패널에서 진행 상태를 확인하세요."
        );
      } else {
        alertContext.showAlert(
          "서류 생성 요청에 실패했습니다.\n고객센터에 문의해주세요."
        );
      }
    } catch (error: any) {
      console.error("서류 생성 오류:", error);
      console.error("에러 응답:", error.response);

      if (error?.response?.status === 429) {
        alertContext.showAlert(error.response.data?.message || "동시 처리 가능한 작업 수를 초과했습니다.");
      } else if (error?.response?.status === 409) {
        alertContext.showAlert(error.response.data?.message || "동일 파일이 이미 처리 중입니다.\n처리가 끝난 뒤 다시 시도해주세요.");
      } else if (isExcelOverloadError(error)) {
        // 처리 시간 초과인지, 서버에 닿지 못한 것인지에 따라 안내가 다르다
        alertContext.showAlert(
          getExcelErrorMessage(
            error,
            "서류 생성에 실패했습니다.\n고객센터에 문의해주세요."
          )
        );
      } else if (error.response) {
        // 서버에서 응답을 받았지만 에러 상태 코드
        alertContext.showAlert(
          `서류 생성 중 오류가 발생했습니다.\n상태 코드: ${
            error.response.status
          }\n${error.response.data?.message || "고객센터에 문의해주세요."}`
        );
      } else if (error.request) {
        // 요청이 전송되었지만 응답을 받지 못함
        alertContext.showAlert(
          "서류 생성 중 네트워크 오류가 발생했습니다.\n인터넷 연결을 확인해주세요."
        );
      } else {
        alertContext.showAlert(
          "서류 생성 중 오류가 발생했습니다.\n고객센터에 문의해주세요."
        );
      }
    }
  };

  // ID 기반 API 요청 생성 (기존 getRequests를 대체)
  const getRequestsByItemId = (
    itemId: string,
    data: { s3Url: string; selectedSheets: number[] }[],
    presets: any
  ) => {
    let macroFunc: any;

    // ID 기반 API 함수 매핑
    switch (itemId) {
      case "change-3": // 두 줄로 나누기
        macroFunc = api.postExcelX2Execute;
        break;
      case "change-2": // 증감내역서
        macroFunc = api.postExcelFluctuationExecute;
        break;
      case "change-1": // 변경실정 보고서
        macroFunc = api.postExcelChangeReportExecute;
        break;
      case "change-7": // 증감+실정보고서
        macroFunc = api.postFluctuationReportDocumentExecute;
        break;
      case "start-1": // 계약/착공내역서
        macroFunc = api.postExcelContractExecute;
        break;
      case "progress-1": // 기성내역서
        macroFunc = api.postExcelReadyExecute;
        break;
      case "change-4": // 변경 준공만들기
        macroFunc = api.postExcelOverrideExecute;
        break;
      case "change-5": // 변경 한줄만들기
        macroFunc = api.postExcelDeleteExecute;
        break;
      case "change-6": // 변경 색상교체
        macroFunc = api.postExcelColorExecute;
        break;
      case "completion-1": // 준공내역서
        macroFunc = api.postExcelX2Execute;
        break;
      case "change-8": // 차수분 내역서
        // 차수분 내역서는 특별한 처리가 필요하므로 여기서는 더미 함수를 사용
        // 실제 처리는 handleUnifiedExecution에서 별도로 처리됨
        return [];
      default:
        throw new Error(`지원하지 않는 항목입니다: ${itemId}`);
    }

    const requests = data.map((d) => {
      // 준공내역서의 경우 coverOption을 'complete'로 하드코딩, 나머지는 사용자 선택값 사용
      let coverOptionValue = gapjiOption;

      if (itemId === "completion-1") {
        // 준공내역서
        coverOptionValue = "complete";
      }

      const baseParams = {
        data: [d],
        originOption,
        divideOption,
        isOriginUpper,
        coverOption: coverOptionValue,
        divideDirection: divideDirectionOption,
        // 두줄 나누기(change-3·completion-1)에서만 의미 있는 옵션. 체크한 경우만 true
        keepOriginFormulas:
          (itemId === "change-3" || itemId === "completion-1") && keepOriginFormulas,
        presets, // 모든 API 호출에 프리셋 데이터 포함
        advancePayment: advancePayment ? advancePayment.replace(/,/g, "") : "", // 선급금 데이터 추가 (콤마 제거)
        completionSettlementAmount: completionSettlementAmount
          ? completionSettlementAmount.replace(/,/g, "")
          : "", // 준공정산금액 데이터 추가 (콤마 제거)
        claimDate: claimDate || "", // 청구 일자 데이터 추가
        claimAmount: claimAmount ? claimAmount.replace(/,/g, "") : "", // 청구 금액 데이터 추가 (콤마 제거)
        // change-2(증감내역서), change-7(증감+실정보고서)일 때만 의미 있는 분리 컬럼 옵션
        useSeparatedColumns:
          itemId === "change-2" || itemId === "change-7"
            ? useSeparatedColumns
            : false,
      };

      return macroFunc(baseParams);
    });
    return requests;
  };

  const handleHiddenSheetClick = () => {
    const newIsHiddenSheetShow = !isHiddenSheetShow;
    setIsHiddenSheetShow(newIsHiddenSheetShow);
    refreshAllEntireChecked(newIsHiddenSheetShow);

    // fileInfos 상태를 강제로 업데이트하여 UI 리렌더링 트리거
    setFileInfos([...fileInfos]);
  };

  const refreshAllEntireChecked = (isHiddenSheetShow: boolean) => {
    const newFileInfos = [...fileInfos];
    newFileInfos.forEach((fileInfo) => {
      fileInfo.isEntireChecked = (fileInfo.sheets ?? []).every((sheet) =>
        isHiddenSheetShow
          ? sheet.checked
          : sheet.isHidden
          ? true
          : sheet.checked
      );
    });
    setFileInfos(newFileInfos);
  };

  // 프리셋 관련 핸들러
  const handleOpenPresetModal = (
    type: "fieldRepresentative" | "companyInfo" | "projectInfo"
  ) => {
    setCurrentPresetType(type);
    setEditingPreset(null);
    setIsPresetModalOpen(true);
  };

  const handleEditPreset = (preset: PresetItem | null) => {
    if (preset) {
      setCurrentPresetType(preset.type);
      setEditingPreset(preset);
      setIsPresetModalOpen(true);
    }
  };

  const handleSavePreset = async (
    presetData: Omit<PresetItem, "id" | "createdAt">,
    editMode?: { id: string; createdAt: string }
  ) => {
    try {
      loading.showLoading("기본정보를 저장하는 중입니다.");

      let response;

      if (editMode) {
        // 편집 모드 - 기존 프리셋 업데이트
        const presetId = parseInt(editMode.id.split("-")[0]);

        if (presetData.type === "fieldRepresentative") {
          const backendData = {
            name: (presetData.data as any).name || "",
            resident_number: (presetData.data as any).residentNumber || "",
            masked_resident_number:
              (presetData.data as any).maskedResidentNumber || "",
            address: (presetData.data as any).address || "",
            birth_date: (presetData.data as any).birthDate || "",
            year: (presetData.data as any).year || "",
            period: (presetData.data as any).period || "",
            position: (presetData.data as any).position || "",
            category: (presetData.data as any).category || "",
            grade: (presetData.data as any).grade || "",
            registration_number:
              (presetData.data as any).registrationNumber || "",
            registration_date: (presetData.data as any).registrationDate || "",
            company_id: (presetData.data as any).companyId
              ? parseInt((presetData.data as any).companyId)
              : null,
          };
          response = await api.updateFieldRepresentative(presetId, backendData);
        } else if (presetData.type === "companyInfo") {
          const backendData = {
            business_number: (presetData.data as any).businessNumber || "",
            corporate_number: (presetData.data as any).corporateNumber || "",
            company_name: (presetData.data as any).companyName || "",
            address: (presetData.data as any).address || "",
            detail_address: (presetData.data as any).detailAddress || "",
            representative: (presetData.data as any).representative || "",
            phone: (presetData.data as any).phone || "",
            fax: (presetData.data as any).fax || "",
            account_number: (presetData.data as any).accountNumber || "",
            deposit_type: (presetData.data as any).depositType || "",
            account_holder: (presetData.data as any).accountHolder || "",
            bank_name: (presetData.data as any).bankName || "",
            branch_name: (presetData.data as any).branchName || "",
          };
          response = await api.updateCompanyInfo(presetId, backendData);
        } else if (presetData.type === "projectInfo") {
          const backendData = {
            project_name: (presetData.data as any).projectName || "",
            project_location: (presetData.data as any).projectLocation || "",
            contract_date: (presetData.data as any).contractDate || "",
            start_date: (presetData.data as any).startDate || "",
            completion_date: (presetData.data as any).completionDate || "",
            project_period: (presetData.data as any).projectPeriod || "",
            performance_period:
              (presetData.data as any).performancePeriod || "",
            expected_completion_date:
              (presetData.data as any).expectedCompletionDate || "",
            contract_amount: (presetData.data as any).contractAmount || "",
            contract_deposit: (presetData.data as any).contractDeposit || "",
            warranty_period: (presetData.data as any).warrantyPeriod || "",
            client: (presetData.data as any).client || "",
            business_type: (presetData.data as any).businessType || "",
            defect_warranty_period:
              (presetData.data as any).defectWarrantyPeriod || "",
            city_info: (presetData.data as any).cityInfo || "",
            ordering_organization:
              (presetData.data as any).orderingOrganization || "",
            completion_settlement_amount:
              (presetData.data as any).completionSettlementAmount || "",
            claim_date: (presetData.data as any).claimDate || "",
            claim_amount: (presetData.data as any).claimAmount || "",
            company_id: (presetData.data as any).companyId
              ? parseInt((presetData.data as any).companyId)
              : null,
          };
          response = await api.updateExcelProjectInfo(presetId, backendData);
        }
      } else {
        // 새로 추가
        if (presetData.type === "fieldRepresentative") {
          const backendData = {
            name: (presetData.data as any).name || "",
            resident_number: (presetData.data as any).residentNumber || "",
            masked_resident_number:
              (presetData.data as any).maskedResidentNumber || "",
            address: (presetData.data as any).address || "",
            birth_date: (presetData.data as any).birthDate || "",
            year: (presetData.data as any).year || "",
            period: (presetData.data as any).period || "",
            position: (presetData.data as any).position || "",
            category: (presetData.data as any).category || "",
            grade: (presetData.data as any).grade || "",
            registration_number:
              (presetData.data as any).registrationNumber || "",
            registration_date: (presetData.data as any).registrationDate || "",
            company_id: (presetData.data as any).companyId
              ? parseInt((presetData.data as any).companyId)
              : null,
          };
          response = await api.createFieldRepresentative(backendData);
        } else if (presetData.type === "companyInfo") {
          const backendData = {
            business_number: (presetData.data as any).businessNumber || "",
            corporate_number: (presetData.data as any).corporateNumber || "",
            company_name: (presetData.data as any).companyName || "",
            address: (presetData.data as any).address || "",
            detail_address: (presetData.data as any).detailAddress || "",
            representative: (presetData.data as any).representative || "",
            phone: (presetData.data as any).phone || "",
            fax: (presetData.data as any).fax || "",
            account_number: (presetData.data as any).accountNumber || "",
            deposit_type: (presetData.data as any).depositType || "",
            account_holder: (presetData.data as any).accountHolder || "",
            bank_name: (presetData.data as any).bankName || "",
            branch_name: (presetData.data as any).branchName || "",
          };
          response = await api.createCompanyInfo(backendData);
        } else if (presetData.type === "projectInfo") {
          const backendData = {
            project_name: (presetData.data as any).projectName || "",
            project_location: (presetData.data as any).projectLocation || "",
            contract_date: (presetData.data as any).contractDate || "",
            start_date: (presetData.data as any).startDate || "",
            completion_date: (presetData.data as any).completionDate || "",
            project_period: (presetData.data as any).projectPeriod || "",
            performance_period:
              (presetData.data as any).performancePeriod || "",
            expected_completion_date:
              (presetData.data as any).expectedCompletionDate || "",
            contract_amount: (presetData.data as any).contractAmount || "",
            contract_deposit: (presetData.data as any).contractDeposit || "",
            warranty_period: (presetData.data as any).warrantyPeriod || "",
            client: (presetData.data as any).client || "",
            business_type: (presetData.data as any).businessType || "",
            defect_warranty_period:
              (presetData.data as any).defectWarrantyPeriod || "",
            city_info: (presetData.data as any).cityInfo || "",
            ordering_organization:
              (presetData.data as any).orderingOrganization || "",
            completion_settlement_amount:
              (presetData.data as any).completionSettlementAmount || "",
            claim_date: (presetData.data as any).claimDate || "",
            claim_amount: (presetData.data as any).claimAmount || "",
            company_id: (presetData.data as any).companyId
              ? parseInt((presetData.data as any).companyId)
              : null,
          };
          response = await api.createProjectInfo(backendData);
        }
      }

      if (response && (response.status === 200 || response.status === 201)) {
        alertContext.showAlert(
          editMode ? "기본정보가 수정되었습니다." : "기본정보가 추가되었습니다."
        );
        await loadPresetsFromBackend(); // 프리셋 목록 새로고침
      }

      loading.closeLoading();
    } catch (error) {
      loading.closeLoading();
      console.error("프리셋 저장 실패:", error);
      alertContext.showAlert("기본정보 저장 중 오류가 발생했습니다.");
    }
  };

  const handleDeletePreset = async (preset: PresetItem) => {
    if (window.confirm(`'${preset.name}' 기본정보를 삭제하시겠습니까?`)) {
      try {
        loading.showLoading("기본정보를 삭제하는 중입니다.");

        const presetId = parseInt(preset.id.split("-")[0]);
        let response;

        if (preset.type === "fieldRepresentative") {
          response = await api.deleteFieldRepresentative(presetId);
        } else if (preset.type === "companyInfo") {
          response = await api.deleteCompanyInfo(presetId);
        } else if (preset.type === "projectInfo") {
          response = await api.deleteExcelProjectInfo(presetId);
        }

        if (response && (response.status === 204 || response.status === 200)) {
          // 삭제된 프리셋이 기본 프리셋으로 설정되어 있다면 기본 프리셋에서 제거
          const storedDefaultPresets = getDefaultPresetsFromStorage();
          if (storedDefaultPresets[preset.type]?.id === preset.id) {
            const updatedDefaultPresets = { ...storedDefaultPresets };
            delete updatedDefaultPresets[preset.type];
            saveDefaultPresetsToStorage(updatedDefaultPresets);
          }

          // 삭제된 프리셋이 현재 선택된 프리셋이라면 선택 해제
          if (selectedPresets[preset.type]?.id === preset.id) {
            const updatedSelectedPresets = { ...selectedPresets };
            delete updatedSelectedPresets[preset.type];
            setSelectedPresets(updatedSelectedPresets);
          }

          alertContext.showAlert("기본정보가 삭제되었습니다.");
          await loadPresetsFromBackend(); // 프리셋 목록 새로고침
        }

        loading.closeLoading();
      } catch (error) {
        loading.closeLoading();
        console.error("프리셋 삭제 실패:", error);
        alertContext.showAlert("기본정보 삭제 중 오류가 발생했습니다.");
      }
    }
  };

  // 컴포넌트 마운트 시 백엔드에서 프리셋 데이터 로드
  useEffect(() => {
    loadPresetsFromBackend();
  }, []);

  // 차수 수 변경 시 텍스트 배열 업데이트
  useEffect(() => {
    const newTexts = Array.from({ length: phaseCount }, (_, i) => 
      phaseTexts[i] || `${i + 1}차`
    );
    setPhaseTexts(newTexts);
  }, [phaseCount]);

  // 백엔드에서 프리셋 데이터 로드
  const loadPresetsFromBackend = async () => {
    try {
      // 3개의 개별 API를 병렬로 호출
      const [fieldRepResponse, companyInfoResponse, projectInfoResponse] =
        await Promise.all([
          api.getFieldRepresentatives().catch((err) => {
            console.error("현장대리인 API 호출 실패:", err);
            return { data: [] };
          }),
          api.getCompanyInfos().catch((err) => {
            console.error("회사정보 API 호출 실패:", err);
            return { data: [] };
          }),
          api.getProjectInfos().catch((err) => {
            console.error("공사정보 API 호출 실패:", err);
            return { data: [] };
          }),
        ]);

      const convertedPresets: PresetItem[] = [];

      // 현장대리인 데이터 변환
      if (fieldRepResponse.data) {
        fieldRepResponse.data.forEach((item: any) => {
          const convertedItem: PresetItem = {
            id: `${item.id}-field`,
            name: item.name || "현장대리인",
            type: "fieldRepresentative" as const,
            data: {
              name: item.name || "",
              residentNumber: item.residentNumber || "",
              maskedResidentNumber: item.maskedResidentNumber || "",
              address: item.address || "",
              birthDate: item.birthDate || "",
              year: item.year || "",
              period: item.period || "",
              position: item.position || "",
              category: item.category || "",
              grade: item.grade || "",
              registrationNumber: item.registrationNumber || "",
              registrationDate: item.registrationDate || "",
              companyId: item.companyId || item.company_id || "",
            },
            createdAt: item.createdAt,
          };
          convertedPresets.push(convertedItem);
        });
      }

      // 회사정보 데이터 변환
      if (companyInfoResponse.data) {
        companyInfoResponse.data.forEach((item: any) => {
          const convertedItem: PresetItem = {
            id: `${item.id}-company`,
            name: item.companyName || item.company_name || "회사정보",
            type: "companyInfo" as const,
            data: {
              businessNumber: item.businessNumber || "",
              corporateNumber: item.corporateNumber || "",
              companyName: item.companyName || "",
              address: item.address || "",
              detailAddress: item.detailAddress || "",
              representative: item.representative || "",
              phone: item.phone || "",
              fax: item.fax || "",
              accountNumber: item.accountNumber || "",
              depositType: item.depositType || "",
              accountHolder: item.accountHolder || "",
              bankName: item.bankName || "",
              branchName: item.branchName || "",
            },
            createdAt: item.createdAt,
          };
          convertedPresets.push(convertedItem);
        });
      }

      // 공사정보 데이터 변환
      if (projectInfoResponse.data) {
        projectInfoResponse.data.forEach((item: any) => {
          const convertedItem: PresetItem = {
            id: `${item.id}-project`,
            name: item.projectName || "공사정보",
            type: "projectInfo" as const,
            data: {
              projectName: item.projectName || "",
              projectLocation: item.projectLocation || "",
              contractDate: item.contractDate || "",
              startDate: item.startDate || "",
              completionDate: item.completionDate || "",
              projectPeriod: item.projectPeriod || "",
              performancePeriod: item.performancePeriod || "",
              expectedCompletionDate: item.expectedCompletionDate || "",
              contractAmount: item.contractAmount || "",
              contractDeposit: item.contractDeposit || "",
              warrantyPeriod: item.warrantyPeriod || "",
              client: item.client || "",
              businessType: item.businessType || "",
              defectWarrantyPeriod: item.defectWarrantyPeriod || "",
              cityInfo: item.cityInfo || "",
              orderingOrganization: item.orderingOrganization || "",
              completionSettlementAmount: item.completionSettlementAmount || "",
              claimDate: item.claimDate || "",
              claimAmount: item.claimAmount || "",
              companyId: item.companyId || item.company_id || "",
            },
            createdAt: item.createdAt,
          };
          convertedPresets.push(convertedItem);
        });
      }

      setPresets(convertedPresets);
    } catch (error) {
      console.error("프리셋 로드 실패:", error);
      // 에러 발생 시 빈 배열로 설정
      setPresets([]);
    }
  };

  // 프리셋 선택 핸들러
  const handleSelectPreset = (
    type: "fieldRepresentative" | "companyInfo" | "projectInfo",
    preset?: PresetItem
  ) => {
    console.log(
      `[CONTAINER] handleSelectPreset 호출: ${type}`,
      preset?.name || "undefined"
    );

    setSelectedPresets((prevSelectedPresets) => {
      const newSelectedPresets = { ...prevSelectedPresets };

      // 회사정보가 변경되면 연결된 현장대리인과 공사정보 초기화
      if (type === "companyInfo") {
        newSelectedPresets.companyInfo = preset;
        newSelectedPresets.fieldRepresentative = undefined;
        newSelectedPresets.projectInfo = undefined;

        // 회사정보가 제거되는 경우 전체 기본 프리셋 초기화
        if (!preset) {
          saveDefaultPresetsToStorage({});
          console.log(`[CONTAINER] 회사정보 해제 - 모든 프리셋 초기화`);
        }
      } else {
        newSelectedPresets[type] = preset;
      }

      console.log(`[CONTAINER] 상태 업데이트:`, {
        이전: prevSelectedPresets,
        새로운: newSelectedPresets,
      });

      return newSelectedPresets;
    });
  };

  // 모든 프리셋 한 번에 초기화하는 핸들러 (UsagePlanForm용)
  const handleClearAllPresets = () => {
    console.log(`[CONTAINER] handleClearAllPresets 호출 - 모든 프리셋 초기화`);

    // 로컬스토리지 먼저 초기화
    saveDefaultPresetsToStorage({});

    // 모든 프리셋을 한 번에 초기화
    setSelectedPresets({
      companyInfo: undefined,
      fieldRepresentative: undefined,
      projectInfo: undefined,
    });

    console.log(`[CONTAINER] 모든 프리셋 초기화 완료`);
  };

  // 시트 선택 탭 토글 핸들러 추가
  const toggleFileExpanded = (fileIndex: number) => {
    const newExpanded = new Set(expandedFiles);
    if (newExpanded.has(fileIndex)) {
      newExpanded.delete(fileIndex);
    } else {
      newExpanded.add(fileIndex);
    }
    setExpandedFiles(newExpanded);
  };

  // 기본 프리셋 설정 핸들러
  const handleSetDefaultPreset = (
    type: "fieldRepresentative" | "companyInfo" | "projectInfo",
    preset?: PresetItem
  ) => {
    const currentDefaults = getDefaultPresetsFromStorage();
    const updatedDefaultPresets = {
      ...currentDefaults,
      [type]: preset,
    };

    saveDefaultPresetsToStorage(updatedDefaultPresets);

    // 현재 계약/착공내역서가 선택되어 있다면 즉시 적용
    if (selectedItemId === "start-1") {
      setSelectedPresets({
        ...selectedPresets,
        [type]: preset,
      });
    }
  };

  // 전체 초기화 핸들러
  const handleReset = () => {
    // 선택된 항목 초기화
    setSelectedItemId("start-1");
    setSelectedCategoryIndex(3); // 계약/착공내역서의 originalIndex

    // 파일 관련 초기화
    setFileInfos([]);
    setExpandedFiles(new Set());
    fileSizeSum.current = 0;
    sheetCellSum.current = 0;

    // 계약/착공내역서 선택 시 기본 프리셋 자동 적용
    const storedDefaults = getDefaultPresetsFromStorage();
    const matchedDefaults = {
      fieldRepresentative: presets.find(
        (p) => p.id === storedDefaults.fieldRepresentative?.id
      ),
      companyInfo: presets.find((p) => p.id === storedDefaults.companyInfo?.id),
      projectInfo: presets.find((p) => p.id === storedDefaults.projectInfo?.id),
    };
    setSelectedPresets(matchedDefaults);

    // 실행 옵션 초기화
    setOriginOption("black");
    setDivideOption("fixed");
    setIsOriginUpper(true);
    setIsHiddenSheetShow(false);
    setGapjiOption("없음");
    setDivideDirectionOption("vertical");
    setIncludeReestimationSheet(true);
  };

  // 안전관리비 사용계획 상태 추가
  const [safetyManagePlan, setSafetyManagePlan] = useState<any>(
    SafetyManagementItemData
  );

  // 환경보전비 사용계획 상태 추가
  const [enviManagePlan, setEnviManagePlan] = useState<any>(
    EnvironmentalItemData
  );

  // 개발/테스트용 샘플 데이터 생성 함수 (실제 운영에서는 제거)
  const createSamplePreset = async () => {
    try {
      // 현장대리인 샘플 데이터
      const fieldRepData = {
        name: "김현장",
        resident_number: "8012341081000",
        masked_resident_number: "801234-1******",
        address: "서울특별시 강남구 테헤란로 123",
        birth_date: "1980-12-34",
        year: "2024",
        period: "2024.01.01 ~ 2024.12.31",
        position: "현장대리인",
        category: "건축",
        grade: "특급",
        registration_number: "2024-001234",
        registration_date: "2024-01-01",
      };

      // 회사정보 샘플 데이터
      const companyData = {
        business_number: "123-45-67890",
        corporate_number: "110111-1234567",
        company_name: "주식회사 오름조경",
        address: "제주특별자치도 서귀포시 중앙로 123",
        detail_address: "오름빌딩 10층",
        representative: "이대표",
        phone: "064-1234-5678",
        fax: "064-1234-5679",
        account_number: "3010233-0358-21",
        deposit_type: "보통예금",
        account_holder: "주식회사 오름조경",
        bank_name: "농협은행",
        branch_name: "서귀포시지부",
      };

      // 공사정보 샘플 데이터
      const projectData = {
        project_name: "강남 래미안 아파트 신축공사",
        project_location: "서울특별시 강남구 역삼동 123번지",
        contract_date: "2024-01-15",
        start_date: "2024-02-01",
        completion_date: "2025-12-31",
        project_period: "22개월",
        performance_period: "2024.02.01 ~ 2025.12.31",
        expected_completion_date: "2025-12-31",
        contract_amount: "50,000,000,000",
        contract_deposit: "2,500,000,000",
        warranty_period: "2년",
        client: "강남구청",
        business_type: "건축공사업",
        defect_warranty_period: "2년",
        city_info: "서울특별시 청렴이행 서약",
        ordering_organization: "강남구청 건축과",
        completion_settlement_amount: "10,000,000",
        claim_date: "2025-06-25",
        claim_amount: "10,000,000",
      };

      // 각각 개별적으로 생성
      await Promise.all([
        api.createFieldRepresentative(fieldRepData),
        api.createCompanyInfo(companyData),
        api.createProjectInfo(projectData),
      ]);

      console.log("샘플 프리셋 생성 완료");
      await loadPresetsFromBackend();
    } catch (error) {
      console.error("샘플 프리셋 생성 실패:", error);
    }
  };

  // Context 업데이트
  const excelContext = useExcelContext();

  // selectedItemId 변경 시 Context 업데이트
  useEffect(() => {
    if (excelContext) {
      excelContext.setSelectedItemId(selectedItemId);
      excelContext.setSelectedItemInfo(getSelectedItemInfo());
      excelContext.setFileInfos(fileInfos);
      excelContext.setRequiresPreset(requiresPreset);
      excelContext.setSafetyManagePlan(safetyManagePlan);
      excelContext.setEnviManagePlan(enviManagePlan);
      excelContext.setScheduleData(scheduleData);
    }
  }, [
    selectedItemId,
    fileInfos,
    safetyManagePlan,
    enviManagePlan,
    scheduleData,
  ]);

  // requiresPreset 함수 별도 업데이트
  useEffect(() => {
    if (excelContext) {
      excelContext.setRequiresPreset(requiresPreset);
    }
  }, [requiresPreset]);

  return (
    <>
    <ExcelPresenter
      fileInfos={fileInfos}
      fileCountLimit={fileCountLimit}
      originOption={originOption}
      coverOption={gapjiOption}
      divideOption={divideOption}
      divideDirection={divideDirectionOption}
      originRowOption={isOriginUpper}
      keepOriginFormulas={keepOriginFormulas}
      onKeepOriginFormulasChange={setKeepOriginFormulas}
      isHiddenSheetShow={isHiddenSheetShow}
      gapjiOption={gapjiOption}
      divideDirectionOption={divideDirectionOption}
      includeReestimationSheet={includeReestimationSheet}
      macroType={macroType}
      macroCategories={orderedCategories}
      selectedCategoryIndex={selectedCategoryIndex}
      selectedItemId={selectedItemId}
      selectedItemInfo={getSelectedItemInfo()}
      presets={presets}
      isPresetModalOpen={isPresetModalOpen}
      currentPresetType={currentPresetType}
      editingPreset={editingPreset}
      selectedPresets={selectedPresets}
      defaultPresets={getDefaultPresetsFromStorage()}
      expandedFiles={expandedFiles}
      advancePayment={advancePayment}
      completionSettlementAmount={completionSettlementAmount}
      claimDate={claimDate}
      claimAmount={claimAmount}
      // 선급금 신청서 관련 props
      contractMaterialCost={contractMaterialCost}
      contractIndirectLaborCost={contractIndirectLaborCost}
      contractExpenses={contractExpenses}
      contractOther={contractOther}
      prepaymentAdjustmentMaterialCost={prepaymentAdjustmentMaterialCost}
      prepaymentAdjustmentIndirectLaborCost={
        prepaymentAdjustmentIndirectLaborCost
      }
      prepaymentExpenses={prepaymentExpenses}
      prepaymentOther={prepaymentOther}
      requiresFile={requiresFile}
      requiresPreset={requiresPreset}
      isFormInput={isFormInput}
      handleDragOver={handleDragOver}
      handleDrop={handleDrop}
      handleChangeFiles={handleChangeFiles}
      handleDelete={handleDelete}
      handleOnDivideChange={handleOnDivideChange}
      handleOnOriginChange={handleOnOriginChange}
      handleOnOriginRowChange={handleOnOriginRowChange}
      handleOnGapjiChange={handleOnGapjiChange}
      handleOnDivideDirectionChange={handleOnDivideDirectionChange}
      handleAdvancePaymentChange={handleAdvancePaymentChange}
      handleCompletionSettlementAmountChange={
        handleCompletionSettlementAmountChange
      }
      handleClaimDateChange={handleClaimDateChange}
      handleClaimAmountChange={handleClaimAmountChange}
      // 선급금 신청서 핸들러들
      handleContractMaterialCostChange={handleContractMaterialCostChange}
      handleContractIndirectLaborCostChange={
        handleContractIndirectLaborCostChange
      }
      handleContractExpensesChange={handleContractExpensesChange}
      handleContractOtherChange={handleContractOtherChange}
      handlePrepaymentAdjustmentMaterialCostChange={
        handlePrepaymentAdjustmentMaterialCostChange
      }
      handlePrepaymentAdjustmentIndirectLaborCostChange={
        handlePrepaymentAdjustmentIndirectLaborCostChange
      }
      handlePrepaymentExpensesChange={handlePrepaymentExpensesChange}
      handlePrepaymentOtherChange={handlePrepaymentOtherChange}
      handleReestimationSheetChange={handleReestimationSheetChange}
      handleEntireCheck={handleEntireCheck}
      handleSheetClick={handleSheetClick}
      handleUncheckNonStatementSheets={handleUncheckNonStatementSheets}
      handleKeepSheetsWithinLimit={handleKeepSheetsWithinLimit}
      handleExecute={handleExecute}
      handleHiddenSheetClick={handleHiddenSheetClick}
      onClickCategory={(index: number) => setSelectedCategoryIndex(index)}
      onSelectItem={handleItemSelect}
      isItemLocked={isItemLocked}
      isCategoryLocked={isCategoryLocked}
      onOpenPresetModal={handleOpenPresetModal}
      onEditPreset={handleEditPreset}
      onSavePreset={handleSavePreset}
      onDeletePreset={handleDeletePreset}
      onClosePresetModal={() => setIsPresetModalOpen(false)}
      onSelectPreset={handleSelectPreset}
      onSetDefaultPreset={handleSetDefaultPreset}
      onClearDefaultPreset={(type) => {
        if (type === "companyInfo") {
          // 회사정보 제거 시 전체 기본 프리셋 초기화
          saveDefaultPresetsToStorage({});
        } else {
          const currentDefaults = getDefaultPresetsFromStorage();
          const updatedDefaults = { ...currentDefaults };
          delete updatedDefaults[type];
          saveDefaultPresetsToStorage(updatedDefaults);
        }
      }}
      onToggleFileExpanded={toggleFileExpanded}
      onReset={handleReset}
      onDocumentGenerate={(
        itemId: string,
        presets?: any,
        additionalData?: any
      ) => {
        return handleUnifiedExecution(itemId, additionalData);
      }}
      safetyManagePlan={safetyManagePlan}
      onSafetyManagePlanChange={setSafetyManagePlan}
      enviManagePlan={enviManagePlan}
      onEnviManagePlanChange={setEnviManagePlan}
      getDefaultPresetsFromStorage={getDefaultPresetsFromStorage}
      saveDefaultPresetsToStorage={saveDefaultPresetsToStorage}
      onClearAllPresets={handleClearAllPresets}
      scheduleData={scheduleData}
      onScheduleDataChange={handleScheduleDataChange}
      // 차수분 내역서 관련 props
      phaseCount={phaseCount}
      phaseTexts={phaseTexts}
      totalText={totalText}
      remainderText={remainderText}
      useExistingFiles={useExistingFiles}
      phaseFiles={phaseFiles}
      isLoadingPhaseSheets={isLoadingPhaseSheets}
      isProcessing={isProcessing}
      phaseFileInputRefs={phaseFileInputRefs}
      onPhaseCountChange={handlePhaseCountChange}
      onPhaseTextsChange={handlePhaseTextsChange}
      onTotalTextChange={handleTotalTextChange}
      onRemainderTextChange={handleRemainderTextChange}
      onUseExistingFilesChange={handleUseExistingFilesChange}
      onPhaseFileChange={handlePhaseFileChange}
      onDeletePhaseFile={handleDeletePhaseFile}
      onPhasedStatementExecution={handlePhasedStatementExecutionFromPresenter}
      onShowAlert={(message: string) => alertContext.showAlert(message)}
      // 증감내역서 분리 컬럼 옵션
      useSeparatedColumns={useSeparatedColumns}
      onUseSeparatedColumnsChange={setUseSeparatedColumns}
    />
    <ExcelTaskPanel tasks={excelTasks} onClear={clearExcelTasks} onCancel={cancelExcelTask} onCancelAll={cancelAllExcelTasks} onDelete={deleteExcelTask} onRetry={retryExcelTask} />
    </>
  );
};

// ExcelProvider로 감싸진 메인 컴포넌트
const ExcelContainer = () => {
  return <ExcelContainerInner />;
};

export default ExcelContainer;
